import { useEffect, useState } from 'react';

import { fetchUploadFile } from './uploadsApi';

// Blob URLs by upload id, kept for the session so scrolling the list does not fetch a picture twice.
const urls = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

/** For tests. */
export const clearUploadImageCache = () => {
    urls.forEach((url) => URL.revokeObjectURL(url));
    urls.clear();
    inFlight.clear();
};

const load = (id: string): Promise<string> => {
    const cached = urls.get(id);
    if (cached) {
        return Promise.resolve(cached);
    }
    const pending = inFlight.get(id);
    if (pending) {
        return pending;
    }
    const request = fetchUploadFile(id)
        .then((blob) => {
            const url = URL.createObjectURL(blob);
            urls.set(id, url);
            return url;
        })
        .finally(() => inFlight.delete(id));
    inFlight.set(id, request);
    return request;
};

/** The picture of an uploaded image, or null while it loads or when it cannot be (a missing file, say). */
export const useUploadImage = (id: string, enabled: boolean): string | null => {
    const [url, setUrl] = useState<string | null>(() => urls.get(id) ?? null);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        let cancelled = false;
        load(id)
            .then((loaded) => {
                if (!cancelled) {
                    setUrl(loaded);
                }
            })
            .catch(() => {
                // No picture: the row keeps its icon.
            });
        return () => {
            cancelled = true;
        };
    }, [id, enabled]);

    return url;
};
